<template>
    <div
        class="multi-select-container"
        :class="{
            'is-open': isOpen,
            'has-selections': selectedItems.length > 0
        }"
    >
        <div
            class="multi-select-input"
            @click="toggleDropdown"
            @blur="handleBlur"
            tabindex="0"
        >
            <template v-if="!textView">
                <div class="selected-items">
                    <span
                        v-for="(item, index) in selectedItems"
                        :key="item.value"
                        class="selected-item"
                    >
                        {{ item.label }}
                        <button
                            @click.stop="removeItem(index)"
                            class="remove-item"
                            type="button"
                        >
                            ×
                        </button>
                    </span>
                </div>
            </template>

            <template v-if="textView">
                <div class="text-view">
                    {{ selectedItems.map(item => item.label).join(", ") }}
                    <span v-if="selectedItems.length === 0" class="placeholder">
                        {{ placeholder }}
                    </span>
                </div>
            </template>

            <template v-if="selectedItems.length === 0">
                <div class="multi-select-arrow" :class="{ 'is-open': isOpen }">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <path d="M6 9l6 6 6-6" />
                    </svg>
                </div>
            </template>

            <template v-if="selectedItems.length > 0">
                <div class="multi-select-close" @click="removeAllItems">
                    <i class="fa fa-times"></i>
                </div>
            </template>
        </div>

        <div v-if="isOpen" class="multi-select-dropdown">
            <div class="options-container">
                <div
                    v-for="option in filteredOptions"
                    :key="option.value"
                    @click="toggleOption(option)"
                    class="option-item"
                    :class="{ 'is-selected': isOptionSelected(option) }"
                >
                    <div class="option-checkbox">
                        <input
                            type="checkbox"
                            :checked="isOptionSelected(option)"
                            class="checkbox-input"
                        />
                    </div>
                    <span class="option-label">{{ option.label }}</span>
                </div>

                <div v-if="filteredOptions.length === 0" class="no-options">
                    {{ noOptionsMessage }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "MultiSelectForm",
    props: {
        options: {
            type: Array,
            default: () => [],
            required: true
        },
        value: {
            type: Array,
            default: () => []
        },
        placeholder: {
            type: String,
            default: "Selecione as opções..."
        },
        noOptionsMessage: {
            type: String,
            default: "Nenhuma opção encontrada"
        },
        required: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        textView: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            isOpen: false,
            selectedItems: [],
            filteredOptions: []
        };
    },
    watch: {
        value: {
            handler(newValue) {
                this.selectedItems = this.getSelectedItemsFromValue(newValue);
            },
            immediate: true
        },
        options: {
            handler() {
                this.filterOptions();
            },
            immediate: true
        }
    },
    mounted() {
        this.filterOptions();
        document.addEventListener("click", this.handleClickOutside);
    },
    beforeDestroy() {
        document.removeEventListener("click", this.handleClickOutside);
    },
    methods: {
        toggleDropdown() {
            if (this.disabled) return;

            this.isOpen = !this.isOpen;
        },

        closeDropdown() {
            this.isOpen = false;
            this.filterOptions();
        },

        handleBlur(event) {
            setTimeout(() => {
                if (!this.$el.contains(event.relatedTarget)) {
                    this.closeDropdown();
                }
            }, 150);
        },

        handleClickOutside(event) {
            if (!this.$el.contains(event.target)) {
                this.closeDropdown();
            }
        },

        filterOptions() {
            this.filteredOptions = this.options;
        },

        toggleOption(option) {
            if (this.disabled) return;

            const index = this.selectedItems.findIndex(
                item => item.value === option.value
            );

            if (index > -1) {
                this.selectedItems.splice(index, 1);
            } else {
                this.selectedItems.push(option);
            }

            this.emitChange();
        },

        isOptionSelected(option) {
            return this.selectedItems.some(item => item.value === option.value);
        },

        removeItem(index) {
            this.selectedItems.splice(index, 1);
            this.emitChange();
        },

        removeAllItems() {
            this.selectedItems = [];
            this.emitChange();
        },

        getSelectedItemsFromValue(value) {
            if (!Array.isArray(value)) return [];

            return this.options.filter(option => value.includes(option.value));
        },

        emitChange() {
            const selectedValues = this.selectedItems.map(item => item.value);
            this.$emit("input", selectedValues);
            this.$emit("change", selectedValues, this.selectedItems);
        }
    }
};
</script>
